<!-- ============================================================ -->
<!-- CONVERSATION MODE — one card at a time during clarification -->
<!-- ============================================================ -->
<div class="execution-plan conversation-mode" *ngIf="mode === 'conversation'">

  <!-- Complexity estimate banner -->
  <div class="complexity-banner" *ngIf="complexityEstimate">
    <span class="badge mr-2" [ngClass]="getPriorityClass(complexityEstimate.complexity)">
      {{ complexityEstimate.complexity | uppercase }}
    </span>
    <span class="complexity-text">
      {{ complexityEstimate.reasoning }}
      <span class="complexity-range" *ngIf="complexityEstimate.expected_questions_range">
        ({{ complexityEstimate.expected_questions_range[0] }}-{{ complexityEstimate.expected_questions_range[1] }} domande stimate)
      </span>
    </span>
  </div>

  <!-- Thinking state -->
  <div class="thinking-state" *ngIf="isThinking && !currentQuestion">
    <div class="thinking-indicator">
      <span class="spinner-border spinner-border-sm"></span>
      <span class="thinking-text">L'agente sta analizzando la storia...</span>
    </div>
    <!-- Show answered count during thinking -->
    <button class="btn btn-link btn-sm answered-link" *ngIf="answeredCards.length > 0"
            (click)="showAnsweredDrawer = !showAnsweredDrawer">
      {{ answeredCards.length }} gia' gestite (vedi)
    </button>
  </div>

  <!-- Current question card -->
  <div class="carousel-card" [ngClass]="cardAnimationClass" *ngIf="currentQuestion && !isThinking">
    <div class="card-header-row">
      <span class="badge mr-2" [ngClass]="getPriorityClass(currentQuestion.ambiguity?.priority)">
        {{ currentQuestion.ambiguity?.priority | uppercase }}
      </span>
      <strong>{{ currentQuestion.ambiguity?.id }}</strong>
      <span class="badge badge-outline-category ml-2" *ngIf="currentQuestion.ambiguity?.category">
        {{ currentQuestion.ambiguity?.category }}
      </span>
      <span class="card-counter ml-auto" *ngIf="answeredCards.length > 0">
        Domanda {{ answeredCards.length + 1 }}
      </span>
    </div>

    <!-- Question (always visible, visual anchor) -->
    <div class="card-question">
      <span class="card-label">Domanda:</span>
      <p>{{ currentQuestion.ambiguity?.question }}</p>
    </div>

    <!-- Source snippet (collapsible, with inline diff preview) -->
    <div class="collapsible-section" [class.source-previewing]="sourcePreviewRewrite" *ngIf="currentQuestion.ambiguity?.source">
      <div class="collapsible-header" (click)="sourceCollapsed = !sourceCollapsed">
        <i class="fa" [ngClass]="sourceCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
        <span class="collapsible-label">Dalla storia</span>
        <span class="collapsible-preview" *ngIf="sourceCollapsed && !sourcePreviewRewrite">
          {{ currentQuestion.ambiguity.source.length > 80
             ? currentQuestion.ambiguity.source.substring(0, 80) + '...'
             : currentQuestion.ambiguity.source }}
        </span>
        <span class="collapsible-preview preview-active" *ngIf="sourceCollapsed && sourcePreviewRewrite">
          modifica in anteprima...
        </span>
      </div>
      <div class="collapsible-body" *ngIf="!sourceCollapsed">
        <!-- Normal view -->
        <p class="source-text" *ngIf="!sourcePreviewRewrite">
          "{{ currentQuestion.ambiguity.source }}"
        </p>
        <!-- Inline diff preview -->
        <div class="source-inline-diff" *ngIf="sourcePreviewRewrite">
          <span class="diff-removed">{{ currentQuestion.ambiguity.source }}</span>
          <span class="diff-arrow">&rarr;</span>
          <span class="diff-added">{{ sourcePreviewRewrite }}</span>
        </div>
        <div class="source-inline-actions" *ngIf="sourcePreviewRewrite">
          <button class="btn btn-sm btn-primary" (click)="confirmAction()">Conferma</button>
          <button class="btn btn-sm btn-outline-secondary" (click)="cancelConfirmation()">Annulla</button>
        </div>
      </div>
    </div>
    <div class="collapsible-section fallback" *ngIf="!currentQuestion.ambiguity?.source && currentQuestion.ambiguity?.category">
      <div class="collapsible-header">
        <span class="collapsible-label">Contesto:</span>
        <span class="collapsible-preview">{{ currentQuestion.ambiguity.category | uppercase }}</span>
      </div>
    </div>

    <!-- Impact (collapsible) -->
    <div class="collapsible-section" *ngIf="currentQuestion.ambiguity?.impact">
      <div class="collapsible-header" (click)="impactCollapsed = !impactCollapsed">
        <i class="fa" [ngClass]="impactCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
        <span class="collapsible-label">Impatto</span>
        <span class="collapsible-preview" *ngIf="impactCollapsed">
          {{ currentQuestion.ambiguity.impact.length > 80
             ? currentQuestion.ambiguity.impact.substring(0, 80) + '...'
             : currentQuestion.ambiguity.impact }}
        </span>
      </div>
      <div class="collapsible-body" *ngIf="!impactCollapsed">
        <p>{{ currentQuestion.ambiguity?.impact }}</p>
      </div>
    </div>

    <!-- KB Evidence (collapsible, with markdown rendering) -->
    <div class="collapsible-section kb-evidence-section" *ngIf="currentQuestion.kb_result">
      <div class="collapsible-header" (click)="kbCollapsed = !kbCollapsed">
        <i class="fa" [ngClass]="kbCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
        <i class="fa fa-file-text-o"></i>
        <a class="kb-file-name clickable" (click)="openKbDocument(currentQuestion.kb_result); $event.stopPropagation()">
          {{ currentQuestion.kb_result.file_name }}
        </a>
        <span class="badge ml-2" [ngClass]="getPriorityClass(currentQuestion.kb_result.confidence)">
          {{ currentQuestion.kb_result.confidence }}
        </span>
      </div>
      <div class="collapsible-body kb-quote" *ngIf="!kbCollapsed"
           [innerHTML]="renderMarkdown(currentQuestion.kb_result.evidence_quote)">
      </div>
    </div>

    <!-- Rewrite Proposals -->
    <div class="proposals-section" *ngIf="!showProposalWriteOwn && !showProposalHints && !showIgnoreConfirm">
      <span class="card-label" *ngIf="currentQuestion.proposals?.length > 0">Proposte di riscrittura:</span>

      <div class="proposals-grid">
        <div class="proposal-item" *ngFor="let p of currentQuestion.proposals"
             [class.proposal-selected]="showConfirmation && pendingResponse?.chosen_proposal?.id === p.id"
             [class.proposal-dimmed]="showConfirmation && pendingResponse?.chosen_proposal?.id !== p.id">
          <div class="proposal-header">
            <span class="proposal-id">{{ p.id }})</span>
            <span class="proposal-source badge badge-outline-category"
                  *ngIf="p.source_type === 'KB'">
              da KB: {{ p.source_file }}
            </span>
            <span class="proposal-source badge badge-outline-category"
                  *ngIf="p.source_type !== 'KB'">
              supposizione
            </span>
          </div>
          <p class="proposal-rewrite">"{{ p.rewrite }}"</p>
          <button class="btn btn-sm" (click)="chooseProposal(p)"
                  [ngClass]="showConfirmation && pendingResponse?.chosen_proposal?.id === p.id
                    ? 'btn-success' : 'btn-primary'">
            {{ showConfirmation && pendingResponse?.chosen_proposal?.id === p.id
               ? 'Selezionata' : 'Scegli ' + p.id }}
          </button>
        </div>
      </div>

      <div class="proposal-actions" *ngIf="!showConfirmation">
        <button class="btn btn-outline-primary btn-sm" (click)="startProposalWriteOwn()">
          Scrivi la tua
        </button>
        <button class="btn btn-outline-secondary btn-sm" *ngIf="currentQuestion.proposals?.length > 0"
                (click)="startProposalHints()">
          Dai indicazioni
        </button>
      </div>
    </div>

    <!-- Write own rewrite -->
    <div class="card-text-input" *ngIf="showProposalWriteOwn && !showConfirmation">
      <label class="text-muted small">Riscrivi questa parte della storia:</label>
      <textarea class="form-control" rows="3" [(ngModel)]="proposalWriteOwnText"
                placeholder="La tua riscrittura..."></textarea>
      <div class="text-input-actions mt-2">
        <button class="btn btn-sm btn-primary mr-2" (click)="submitProposalWriteOwn()"
                [disabled]="!proposalWriteOwnText.trim()">Conferma</button>
        <button class="btn btn-sm btn-outline-secondary"
                (click)="showProposalWriteOwn = false">Annulla</button>
      </div>
    </div>

    <!-- Give hints for regeneration -->
    <div class="card-text-input" *ngIf="showProposalHints && !showConfirmation">
      <label class="text-muted small">Dai indicazioni all'agente per migliorare le proposte:</label>
      <textarea class="form-control" rows="3" [(ngModel)]="proposalHintsText"
                placeholder="Es: aggiungi anche il campo locale..."></textarea>
      <div class="text-input-actions mt-2">
        <button class="btn btn-sm btn-primary mr-2" (click)="submitProposalHints()"
                [disabled]="!proposalHintsText.trim()">Rigenera</button>
        <button class="btn btn-sm btn-outline-secondary"
                (click)="showProposalHints = false">Annulla</button>
      </div>
    </div>

    <!-- (Confirmation is now inline in the source snippet above) -->

    <!-- KB actions + Ignore (as subtle links) -->
    <div class="card-footer-actions" *ngIf="!showFilePicker && !showUploadPanel && !showIgnoreConfirm">
      <div class="kb-actions-row">
        <a class="kb-action-link" *ngIf="kbFiles.length > 0" (click)="openFilePicker()">
          <i class="fa fa-search"></i> Cerca in altro documento
        </a>
        <a class="kb-action-link" (click)="showUploadPanel = true">
          <i class="fa fa-upload"></i> Carica documento
        </a>
      </div>
      <a class="ignore-link" (click)="requestIgnore()">
        <i class="fa fa-exclamation-triangle"></i> Ignora questa domanda
      </a>
    </div>

    <!-- Upload panel -->
    <div class="upload-panel" *ngIf="showUploadPanel">
      <h6 class="picker-title">Carica documento nella KB</h6>

      <!-- File selection (when not uploading) -->
      <div *ngIf="uploadStatus === 'idle'">
        <div class="upload-area" (click)="fileInput.click()">
          <i class="fa fa-cloud-upload"></i>
          <span *ngIf="!uploadFile">Clicca per selezionare un file</span>
          <span *ngIf="uploadFile" class="selected-file">{{ uploadFile.name }}</span>
          <small class="text-muted">.docx .txt .html .pdf .md</small>
        </div>
        <input #fileInput type="file" hidden
               accept=".docx,.txt,.html,.pdf,.md,.csv,.json"
               (change)="onUploadFileSelected($event)">
        <div class="picker-actions">
          <button class="btn btn-sm btn-outline-secondary"
                  (click)="showUploadPanel = false; uploadFile = null">Annulla</button>
          <button class="btn btn-sm btn-primary" [disabled]="!uploadFile"
                  (click)="triggerUpload()">Carica</button>
        </div>
      </div>

      <!-- Progress indicator -->
      <div class="upload-progress" *ngIf="uploadStatus !== 'idle'">
        <div class="progress-step" [class.active]="uploadStatus === 'uploading'"
             [class.done]="uploadStatus !== 'uploading'">
          <span class="spinner-border spinner-border-sm" *ngIf="uploadStatus === 'uploading'"></span>
          <i class="fa fa-check" *ngIf="uploadStatus !== 'uploading'"></i>
          Caricamento...
        </div>
        <div class="progress-step" [class.active]="uploadStatus === 'indexing'"
             [class.done]="uploadStatus === 'searching' || uploadStatus === 'done'">
          <span class="spinner-border spinner-border-sm" *ngIf="uploadStatus === 'indexing'"></span>
          <i class="fa fa-check" *ngIf="uploadStatus === 'searching' || uploadStatus === 'done'"></i>
          Indicizzazione...
        </div>
        <div class="progress-step" [class.active]="uploadStatus === 'searching'"
             [class.done]="uploadStatus === 'done'">
          <span class="spinner-border spinner-border-sm" *ngIf="uploadStatus === 'searching'"></span>
          <i class="fa fa-check" *ngIf="uploadStatus === 'done'"></i>
          Ricerca risposta...
        </div>
        <div class="progress-step done" *ngIf="uploadStatus === 'done'">
          <i class="fa fa-check-circle"></i> Completato
        </div>
        <div class="progress-step error" *ngIf="uploadStatus === 'error'">
          <i class="fa fa-times-circle"></i> Errore
        </div>
      </div>
    </div>

    <!-- File picker (multi-select) -->
    <div class="file-picker" *ngIf="showFilePicker">
      <h6 class="picker-title">Seleziona documenti dalla KB</h6>
      <div class="picker-list">
        <div class="picker-item" *ngFor="let file of kbFiles"
             [class.selected]="selectedPickerFiles.has(file.name)"
             (click)="togglePickerFile(file.name)">
          <i class="fa" [ngClass]="selectedPickerFiles.has(file.name) ? 'fa-check-square-o' : 'fa-square-o'"></i>
          <span>{{ file.name }}</span>
        </div>
      </div>
      <div class="picker-actions">
        <button class="btn btn-sm btn-outline-secondary" (click)="showFilePicker = false">Annulla</button>
        <button class="btn btn-sm btn-primary" (click)="confirmPickerSearch()"
                [disabled]="selectedPickerFiles.size === 0 || pickerSearching">
          <span class="spinner-border spinner-border-sm mr-1" *ngIf="pickerSearching"></span>
          {{ pickerSearching ? 'Ricerca...' : 'Conferma (' + selectedPickerFiles.size + ')' }}
        </button>
      </div>
    </div>

    <!-- Ignore confirmation panel -->
    <div class="ignore-confirm-panel" *ngIf="showIgnoreConfirm">
      <div class="ignore-confirm-message">
        <i class="fa fa-exclamation-triangle"></i>
        <span>Ignorando questa domanda, l'agente non la considerera' per requisiti e test. Sei sicuro?</span>
      </div>
      <div class="ignore-confirm-actions">
        <button class="btn btn-sm btn-danger" (click)="ignoreQuestion()">
          Si, ignora
        </button>
        <button class="btn btn-sm btn-outline-secondary" (click)="cancelIgnore()">
          Annulla
        </button>
      </div>
    </div>
  </div>

  <!-- Timeline drawer -->
  <div class="answered-drawer" *ngIf="showAnsweredDrawer && answeredCards.length > 0">
    <h6 class="drawer-title">Cronologia intervista ({{ answeredCards.length }})</h6>

    <div class="timeline-item" *ngFor="let card of answeredCards; let i = index"
         [class.active]="selectedTimelineStep === i"
         (click)="selectTimelineStep(i)">
      <div class="timeline-item-header">
        <span class="timeline-step-num" [class.active]="selectedTimelineStep === i">{{ i + 1 }}</span>
        <strong>{{ card.ambiguity?.id }}</strong>
        <span class="badge ml-1" [ngClass]="getActionBadgeClass(card.user_action)">
          {{ getActionLabel(card.user_action) }}
        </span>
        <button class="btn btn-link btn-sm ml-auto timeline-detail-btn"
                (click)="openStepDetail(i, $event)" title="Dettaglio">
          <i class="fa fa-info-circle"></i>
        </button>
      </div>
      <p class="timeline-item-question">{{ card.ambiguity?.question }}</p>

      <!-- Inline story viewer -->
      <div class="story-viewer-inline" *ngIf="selectedTimelineStep === i" (click)="$event.stopPropagation()">
        <div class="viewer-toggle">
          <button class="btn btn-sm"
                  [class.btn-primary]="storyViewerMode === 'evolved'"
                  [class.btn-outline-secondary]="storyViewerMode !== 'evolved'"
                  (click)="storyViewerMode = 'evolved'">Modifiche</button>
          <button class="btn btn-sm"
                  [class.btn-primary]="storyViewerMode === 'original'"
                  [class.btn-outline-secondary]="storyViewerMode !== 'original'"
                  (click)="storyViewerMode = 'original'">Originale</button>
        </div>
        <div class="viewer-content" *ngIf="storyViewerMode === 'evolved'">
          <span *ngFor="let seg of storyDiffSegments"
                [ngClass]="{'diff-added': seg.type === 'added', 'diff-removed': seg.type === 'removed'}">{{ seg.text }}</span>
        </div>
        <div class="viewer-content" *ngIf="storyViewerMode === 'original'">
          {{ originalStory }}
        </div>
      </div>
    </div>
  </div>

  <!-- Step detail modal -->
  <div class="step-detail-overlay" *ngIf="showStepDetailModal && stepDetailCard"
       (click)="closeStepDetail()">
    <div class="step-detail-modal" (click)="$event.stopPropagation()">
      <div class="detail-header">
        <strong>{{ stepDetailCard.ambiguity?.id }}</strong>
        <span class="badge badge-outline-category ml-2">{{ stepDetailCard.ambiguity?.category }}</span>
        <button class="btn btn-link ml-auto" (click)="closeStepDetail()">
          <i class="fa fa-times"></i>
        </button>
      </div>
      <div class="detail-row">
        <span class="detail-label">Domanda:</span>
        <p>{{ stepDetailCard.ambiguity?.question }}</p>
      </div>
      <div class="detail-row" *ngIf="stepDetailCard.ambiguity?.impact">
        <span class="detail-label">Impatto:</span>
        <p>{{ stepDetailCard.ambiguity?.impact }}</p>
      </div>
      <div class="detail-row" *ngIf="stepDetailCard.kb_result">
        <span class="detail-label">KB Evidence:</span>
        <p class="kb-ref">{{ stepDetailCard.kb_result.file_name }}</p>
        <div class="kb-quote" [innerHTML]="renderMarkdown(stepDetailCard.kb_result.evidence_quote)"></div>
      </div>
      <div class="detail-row">
        <span class="detail-label">Azione:</span>
        <span class="badge" [ngClass]="getActionBadgeClass(stepDetailCard.user_action)">
          {{ getActionLabel(stepDetailCard.user_action) }}
        </span>
      </div>
      <div class="detail-row" *ngIf="stepDetailCard.user_text">
        <span class="detail-label">Risposta:</span>
        <p>{{ stepDetailCard.user_text }}</p>
      </div>
    </div>
  </div>

  <!-- Bottom bar: timeline link + proceed button -->
  <div class="conversation-bottom-bar" *ngIf="answeredCards.length > 0">
    <button class="btn btn-link btn-sm" (click)="showAnsweredDrawer = !showAnsweredDrawer">
      {{ answeredCards.length }} gia' gestite ({{ showAnsweredDrawer ? 'chiudi' : 'vedi' }})
    </button>
    <button class="btn btn-primary btn-sm proceed-btn" (click)="proceedWithTests()">
      Procedi con i test
    </button>
  </div>
</div>


<!-- ============================================================ -->
<!-- FULL MODE — existing plan view (S5) -->
<!-- ============================================================ -->
<div class="execution-plan" *ngIf="mode === 'full' && plan">

  <!-- Header -->
  <div class="plan-header">
    <h5 class="plan-title">Piano di Esecuzione</h5>
    <span class="badge ml-2" [ngClass]="getComplexityBadgeClass()">
      {{ plan.complexity | uppercase }}
    </span>
  </div>
  <p class="plan-reasoning">{{ plan.reasoning }}</p>

  <!-- Section 1: Auto-Resolved Ambiguities -->
  <div class="plan-section" *ngIf="resolutions.length > 0">
    <h6 class="section-title">Ambiguita' Auto-Risolte ({{ resolutions.length }})</h6>

    <div class="resolution-card" *ngFor="let r of resolutions">
      <div class="resolution-header">
        <span class="badge mr-2" [ngClass]="getConfidenceBadgeClass(r.confidence)">
          {{ r.confidence | uppercase }}
        </span>
        <strong>{{ r.ambiguity_id }}</strong>
        <span class="resolution-status ml-auto" *ngIf="r.user_action !== 'pending'">
          <span *ngIf="r.user_action === 'approved'" class="text-success">Approvata</span>
          <span *ngIf="r.user_action === 'modified'" class="text-primary">Modificata</span>
          <span *ngIf="r.user_action === 'rejected'" class="text-danger">Rifiutata</span>
        </span>
      </div>

      <!-- Original ambiguity question -->
      <div class="resolution-question">
        <span class="resolution-label">Domanda:</span>
        <p>{{ getAmbiguityById(r.ambiguity_id)?.clarification_needed }}</p>
      </div>

      <!-- Proposed resolution -->
      <div class="resolution-answer" *ngIf="editingResolutionId !== r.ambiguity_id">
        <span class="resolution-label">Risposta:</span>
        <p>{{ r.user_action === 'modified' ? r.user_modified_text : r.proposed_text }}</p>
      </div>

      <!-- Edit mode -->
      <div class="resolution-edit" *ngIf="editingResolutionId === r.ambiguity_id">
        <textarea class="form-control" rows="3" [(ngModel)]="editText"></textarea>
        <div class="edit-actions mt-2">
          <button class="btn btn-sm btn-primary mr-2" (click)="saveEditResolution(r)">Salva</button>
          <button class="btn btn-sm btn-outline-secondary" (click)="cancelEdit()">Annulla</button>
        </div>
      </div>

      <!-- Sources -->
      <div class="resolution-sources" *ngIf="r.sources?.length > 0">
        <span class="resolution-label">Fonte:</span>
        <div class="source-item" *ngFor="let src of r.sources">
          <span class="source-file">{{ src.file_name }}</span>
          <span class="source-quote" *ngIf="src.evidence_quote">
            "{{ src.evidence_quote.length > 120 ? src.evidence_quote.substring(0, 120) + '...' : src.evidence_quote }}"
          </span>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="resolution-actions" *ngIf="r.user_action === 'pending' || !r.user_action">
        <button class="btn btn-outline-success mr-2" (click)="approveResolution(r)">
          Approva
        </button>
        <button class="btn btn-outline-primary mr-2" (click)="startEditResolution(r)">
          Modifica
        </button>
        <button class="btn btn-outline-danger" (click)="rejectResolution(r)">
          Rifiuta
        </button>
      </div>
    </div>
  </div>

  <!-- Section 2: Unresolved Ambiguities -->
  <div class="plan-section" *ngIf="unresolvedAmbiguities.length > 0">
    <h6 class="section-title">Ambiguita' Irrisolte ({{ unresolvedAmbiguities.length }})</h6>

    <div class="unresolved-card" *ngFor="let amb of unresolvedAmbiguities">
      <div class="unresolved-header">
        <span class="badge badge-secondary mr-2">{{ amb.id }}</span>
        <span class="badge badge-outline-warning" *ngIf="amb.type">{{ amb.type }}</span>
      </div>
      <p class="unresolved-question">{{ amb.clarification_needed }}</p>
      <small class="text-muted">Impatto: {{ amb.impact }}</small>
    </div>
  </div>

  <!-- Section 3: Planned Steps -->
  <div class="plan-section" *ngIf="steps && steps.length > 0">
    <h6 class="section-title">Step Previsti</h6>

    <div class="step-list">
      <div class="step-item" *ngFor="let step of steps"
           [ngClass]="getStepClass(step)"
           (click)="toggleStep(step)">
        <i class="step-icon" [ngClass]="getStepIcon(step)"></i>
        <span class="step-name">{{ step.name }}</span>
        <span class="step-skip-reason" *ngIf="step.status === 'skipped' && step.skip_reason && !step.user_override">
          {{ step.skip_reason }}
        </span>
        <span class="step-override" *ngIf="step.user_override === 'force_include'">
          Forzato
        </span>
        <span class="step-override" *ngIf="step.user_override === 'force_skip'">
          Skippato
        </span>
      </div>
    </div>
  </div>

  <!-- Story version selector -->
  <div class="plan-section" *ngIf="answeredCards.length > 0">
    <h6 class="section-title">Versione storia per la pipeline</h6>
    <div class="version-options">
      <label class="version-option" [class.selected]="selectedStoryVersion === 'final'">
        <input type="radio" name="storyVersion" value="final"
               [(ngModel)]="selectedStoryVersion" (ngModelChange)="onStoryVersionChange()">
        Storia evoluta (dopo tutte le riscritture)
      </label>
      <label class="version-option" [class.selected]="selectedStoryVersion === 'original'">
        <input type="radio" name="storyVersion" value="original"
               [(ngModel)]="selectedStoryVersion" (ngModelChange)="onStoryVersionChange()">
        Storia originale
      </label>
      <ng-container *ngFor="let card of answeredCards; let i = index">
        <label class="version-option"
               [class.selected]="'' + selectedStoryVersion === '' + i"
               *ngIf="card.user_action !== 'ignored' && card.evolved_story">
          <input type="radio" name="storyVersion" [value]="i"
                 [(ngModel)]="selectedStoryVersion" (ngModelChange)="onStoryVersionChange()">
          Dopo passo {{ i + 1 }} ({{ card.ambiguity?.id }})
        </label>
      </ng-container>
    </div>
  </div>

  <!-- Action bar -->
  <div class="plan-actions">
    <button class="btn btn-primary mr-2"
            [disabled]="!allResolutionsActioned && resolutions.length > 0"
            (click)="onApprove()">
      Approva e Procedi
    </button>
    <button class="btn btn-outline-secondary" (click)="onForceFullFlow()">
      Forza Flusso Completo
    </button>
  </div>

</div>
